import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Flex,
  Image,
  Tag,
  Container,
  SimpleGrid,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  VStack,
  Icon,
} from "@chakra-ui/react";
import { MdLocalOffer } from "react-icons/md";
import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import { FiFileText } from "react-icons/fi";
import MarkdownRenderer from "../../Common/MarkdownRenderer";

const POSTS_PER_PAGE = 6;

const AllBlogPosts = ({
  blogPosts = [],
  loading = false,
  error = null,
  fromWhere = "blog", // New prop to distinguish between blog and team activity
  skipCount = 0, // New prop to skip first N posts
  initialPage = 1, // New prop to set initial page
}) => {
  const [page, setPage] = useState(initialPage);
  const navigate = useNavigate();

  // Update page when initialPage changes
  useEffect(() => {
    setPage(initialPage);
  }, [initialPage]);

  // Helper function to check if URL is a PDF
  const isPdfFile = (url) => {
    if (!url) return false;
    const urlLower = url.toLowerCase();
    return urlLower.endsWith('.pdf') || urlLower.includes('.pdf');
  };

  // Helper function to open PDF in new window
  const openPdfInNewWindow = (pdfUrl) => {
    try {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to open PDF:', error);
    }
  };

  // Show all posts without any filtering, but skip first N posts if specified
  const filteredPosts = (blogPosts || []).slice(skipCount);

  // Use all posts
  const posts = filteredPosts;
  const TOTAL_PAGES = Math.ceil(posts.length / POSTS_PER_PAGE);
  const paginatedPosts = posts.slice(
    (page - 1) * POSTS_PER_PAGE,
    page * POSTS_PER_PAGE
  );

  // Handle loading state
  if (loading) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Flex justifyContent="center" alignItems="center" minH="300px">
          <Spinner size="xl" color="#000B8C" />
        </Flex>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Text>{error}</Text>
        </Alert>
      </Container>
    );
  }

  // Handle empty data
  if (posts.length === 0) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Text textAlign="center" color="gray.500" fontSize="lg">
          No blog posts available at the moment.
        </Text>
      </Container>
    );
  }

  const handleCardClick = (blogPost, index) => {
    // Use blog ID or slug if available, otherwise fall back to index
    const blogId = blogPost.id || blogPost.slug || index + 1;

    if (fromWhere === "team_activity") {
      navigate(`/team-activity/${blogId}`, {
        state: { blogPost, currentPage: page },
      });
    } else {
      navigate(`/newsroom-details/${blogId}`, {
        state: { blogPost, currentPage: page },
      });
    }
  };

  // Helper function to check if post has valid media
  const hasValidMedia = (post) => {
    const isPdf = post.image && 
      (post.image.toLowerCase().endsWith('.pdf') || 
       post.image.toLowerCase().includes('.pdf'));
    const hasValidImage =
      post.image &&
      !post.image.includes("/clients/") &&
      !post.image.includes("/png/placeholder.png") &&
      !post.image.includes("placeholder") &&
      !isPdf; // Exclude PDFs from image handling
    const hasValidVideo = post.isVideo && post.videoUrl;
    return hasValidImage || hasValidVideo || isPdf;
  };

  // Helper component for rendering markdown text
  const MarkdownText = ({ children, fontSize, color, noOfLines, ...props }) => {
    const hasMarkdown =
      children &&
      (children.includes("**") ||
        children.includes("*") ||
        children.includes("`") ||
        children.includes("#") ||
        children.includes("[") ||
        children.includes(">") ||
        children.includes("|") ||
        children.includes("---") ||
        children.includes("http") ||
        children.includes("<a"));

    if (!hasMarkdown) {
      return (
        <Text
          fontSize={fontSize}
          color={color}
          noOfLines={noOfLines}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="normal"
          {...props}
        >
          {children}
        </Text>
      );
    }

    return (
      <Box
        fontSize={fontSize}
        color={color}
        noOfLines={noOfLines}
        overflow="hidden"
        sx={{
          "& p": {
            margin: "0",
            lineHeight: "inherit",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            margin: "0",
            fontWeight: "bold",
            lineHeight: "inherit",
            fontSize: "inherit",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& *": {
            wordBreak: "break-word",
          },
        }}
        {...props}
      >
        <MarkdownRenderer content={children} />
      </Box>
    );
  };

  // Pagination numbers logic
  const getPageNumbers = () => {
    const pages = [];
    if (TOTAL_PAGES <= 7) {
      for (let i = 1; i <= TOTAL_PAGES; i++) pages.push(i);
    } else {
      if (page <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", TOTAL_PAGES);
      } else if (page >= TOTAL_PAGES - 3) {
        pages.push(
          1,
          "...",
          TOTAL_PAGES - 4,
          TOTAL_PAGES - 3,
          TOTAL_PAGES - 2,
          TOTAL_PAGES - 1,
          TOTAL_PAGES
        );
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", TOTAL_PAGES);
      }
    }
    return pages;
  };

  return (
    <Container
      maxW={{ base: "100%", md: "7xl" }}
      px={{ base: 2, sm: 4, md: 8 }}
      py={{ base: 4, md: 16 }}
    >
      <Heading
        as="h2"
        fontSize={{ base: "xl", sm: "2xl", md: "4xl" }}
        color="#000B8C"
        mb={{ base: 4, md: 8 }}
        textAlign="left"
        fontWeight="semibold"
      >
        All news posts
      </Heading>
      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        spacing={{ base: 4, sm: 6, md: 8 }}
      >
        {paginatedPosts.map((item, idx) => (
          <Box
            key={idx}
            bg="white"
            borderRadius="20px"
            boxShadow="sm"
            border="1px solid #d1d5db"
            p={{ base: 3, sm: 4, md: 5 }}
            display="flex"
            flexDirection={{ base: "column", sm: "row" }}
            gap={{ base: 3, sm: 4 }}
            minH={{ base: "200px", sm: "220px" }}
            maxH={{ base: "auto", sm: "240px" }}
            cursor="pointer"
            transition="all 0.2s"
            overflow="hidden"
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "lg",
              borderColor: "#25575F",
            }}
            onClick={() =>
              handleCardClick(item, (page - 1) * POSTS_PER_PAGE + idx)
            }
          >
            {/* Image Section - Left Side */}
            {hasValidMedia(item) && (
              <Box 
                position="relative" 
                flexShrink={0}
                w={{ base: "100%", sm: "160px", lg: "160px" }}
                h={{ base: "160px", sm: "160px", lg: "180px" }}
                overflow="hidden"
              >
                {item.isVideo && item.videoUrl ? (
                  <>
                    <video
                      src={item.videoUrl}
                      style={{
                        borderRadius: "16px",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      muted
                      autoPlay
                      loop
                    />
                    <Box
                      position="absolute"
                      top={2}
                      right={2}
                      bg="rgba(0, 0, 0, 0.7)"
                      color="white"
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      VIDEO
                    </Box>
                  </>
                ) : isPdfFile(item.image) ? (
                  <Box
                    borderRadius="16px"
                    bg="gray.50"
                    w="100%"
                    h="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    border="2px dashed #e2e8f0"
                    cursor="pointer"
                    transition="all 0.2s ease"
                    _hover={{
                      bg: "gray.100",
                      borderColor: "#000B8C",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openPdfInNewWindow(item.image);
                    }}
                  >
                    <VStack spacing={1}>
                      <Icon as={FiFileText} boxSize={6} color="#000B8C" />
                      <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                        PDF
                      </Text>
                    </VStack>
                  </Box>
                ) : (
                  <Image
                    src={item.image}
                    alt={item.title}
                    borderRadius="16px"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                )}
              </Box>
            )}

            {/* Content Section - Right Side */}
            <Box 
              flex="1" 
              display="flex" 
              flexDirection="column" 
              minW={0}
              h="100%"
              overflow="hidden"
            >
              <Box mb={2} minH="28px" display="flex" alignItems="center">
                <Flex align="center" gap={2} flexWrap="wrap">
                  <Box 
                    as={MdLocalOffer} 
                    color="#25575F" 
                    flexShrink={0}
                    w="16px"
                    h="16px"
                  />
                  <Tag
                    color="#25575F"
                    bg="#F8F9F6"
                    fontWeight="medium"
                    fontSize={{ base: "xs", sm: "sm" }}
                    flexShrink={0}
                    size="sm"
                    borderRadius="md"
                  >
                    {(() => {
                      const tagValue = typeof item.tag === "object"
                        ? item.tag?.fullName || item.tag?.name
                        : item.tag;
                      return tagValue || "News";
                    })()}
                  </Tag>
                </Flex>
              </Box>
              
              {/* Title Section - Fixed 2 lines or 60 words max */}
              <Box maxH="60px" overflow="hidden" mb={2}>
                <Text
                  fontSize={{ base: "md", sm: "lg", md: "xl" }}
                  color="#222"
                  fontWeight="bold"
                  noOfLines={2}
                  lineHeight="1.4"
                  wordBreak="break-word"
                  overflow="hidden"
                  display="-webkit-box"
                  sx={{
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {(() => {
                    const title = item.title || "";
                    const words = title.split(' ');
                    return words.length > 15 ? words.slice(0, 15).join(' ') + '...' : title;
                  })()}
                </Text>
              </Box>
              
              {/* Description Section - Fixed 3 lines max */}
              <Box maxH="54px" overflow="hidden">
                <MarkdownText
                  color="gray.600"
                  fontSize={{ base: "xs", sm: "sm" }}
                  noOfLines={3}
                  lineHeight="1.4"
                  overflow="hidden"
                  wordBreak="break-word"
                  display="-webkit-box"
                  sx={{
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {item.post_desc || item.desc || "No description available"}
                </MarkdownText>
              </Box>
            </Box>
          </Box>
        ))}
      </SimpleGrid>
      {/* Pagination */}
      <Box
        borderTop="1px solid #f1f2f6"
        mt={{ base: 6, md: 12 }}
        pt={{ base: 4, md: 6 }}
      >
        <Flex
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align={{ base: "stretch", sm: "center" }}
          gap={2}
        >
          <Button
            variant="ghost"
            leftIcon={<ArrowLeftIcon />}
            isDisabled={page === 1}
            onClick={() => setPage(page - 1)}
            colorScheme="gray"
            fontWeight="medium"
            color="#6b7280"
            _hover={{ bg: "gray.100", color: "#4f46e5" }}
            px={4}
            mb={{ base: 2, sm: 0 }}
          >
            Previous
          </Button>
          <Flex
            gap={1}
            align="center"
            flexWrap="wrap"
            justify={{ base: "center", sm: "flex-start" }}
          >
            {getPageNumbers().map((num, idx) =>
              num === "..." ? (
                <Box key={idx} px={2} color="gray.400">
                  ...
                </Box>
              ) : (
                <Button
                  key={idx}
                  variant={num === page ? "solid" : "ghost"}
                  bg={num === page ? "purple.50" : "transparent"}
                  color={num === page ? "#7c3aed" : "#6b7280"}
                  fontWeight={num === page ? "bold" : "normal"}
                  borderRadius="xl"
                  px={num === page ? 4 : 2}
                  onClick={() => setPage(num)}
                  _hover={{
                    bg: num === page ? "purple.100" : "gray.100",
                    color: "#7c3aed",
                  }}
                  fontSize={{ base: "sm", sm: "md" }}
                >
                  {num}
                </Button>
              )
            )}
          </Flex>
          <Button
            variant="ghost"
            rightIcon={<ArrowRightIcon />}
            isDisabled={page === TOTAL_PAGES}
            onClick={() => setPage(page + 1)}
            colorScheme="gray"
            fontWeight="medium"
            color="#6b7280"
            _hover={{ bg: "gray.100", color: "#4f46e5" }}
            px={4}
            mt={{ base: 2, sm: 0 }}
          >
            Next
          </Button>
        </Flex>
      </Box>
    </Container>
  );
};

export default AllBlogPosts;
